"use client";
import React, { useState, useEffect, useCallback } from "react";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import { Thumb } from "./Thumbs";
import VideoComponent from "./VideoComponent";
import { FaTimes } from "react-icons/fa";
import { MdArrowForwardIos } from "react-icons/md";

function getYouTubeThumbnailURL(url: string) {
  const regex =
    /(?:youtube\.com\/(?:.*v=|embed\/|shorts\/)|youtu\.be\/)([^&?/]+)/;
  const match = url.match(regex);

  if (match) {
    const videoID = match[1];
    return `https://img.youtube.com/vi/${videoID}/hqdefault.jpg`; // Max resolution thumbnail
  } else {
    return null; // Return null if the video ID is not found
  }
}

// Example usage
// const url = "https://www.youtube.com/watch?v=5MvEtL1wCX8";
// const videoID = getYouTubeVideoID(url);


type PropType = {
  slides: string[];
  options?: EmblaOptionsType;
  url?: string;
  variant?: boolean;
  loading?: boolean;
};

const EmblaCarousel: React.FC<PropType> = (props) => {
  const { slides, options, url, variant, loading } = props;
  console.log({ url });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [emblaMainRef, emblaMainApi] = useEmblaCarousel(options);
  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
  });
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!emblaMainApi || !emblaThumbsApi) return;
      emblaMainApi.scrollTo(index);
    },
    [emblaMainApi, emblaThumbsApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
    setCanScrollPrev(emblaMainApi.canScrollPrev());
    setCanScrollNext(emblaMainApi.canScrollNext());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();
    emblaMainApi.on("select", onSelect).on("reInit", onSelect);
  }, [emblaMainApi, onSelect]);

  const [open, setOpen] = useState(false);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[500px] w-full">
        <div className="loader-spinner"></div>
      </div>
    );
  }
  return (
    <>
      <div className="embla flex flex-col gap-4">
        <div className="first w-full relative">
          {selectedIndex !== slides.length && (
            <div className="absolute flex gap-1 bottom-4 z-[10] w-max  items-end text-white left-[1rem] bg-themeColor rounded-md p-2 ">
              <CameraIcon />
              <span>
                {url ? (
                  <span>
                    {selectedIndex + 1} / {slides.length + 1}
                  </span>
                ) : (
                  <span>
                    {selectedIndex + 1} / {slides.length}
                  </span>
                )}
              </span>
            </div>
          )}

          <button
            onClick={() => emblaMainApi && emblaMainApi.scrollPrev()}
            className={`transform top-1/2 z-[10] absolute left-4  -translate-y-1/2  rotate-[180deg] ${
              canScrollPrev ? "visible" : "invisible"
            }`}
          >
            <ButtonControls />
          </button>
          <button
            onClick={() => {
              if (emblaMainApi) {
                emblaMainApi.scrollNext();
              }
            }}
            className={`top-1/2 right-4 absolute z-[10]  -translate-y-1/2 ${
              canScrollNext ? "visible" : "invisible"
            }`}
          >
            <ButtonControls />
          </button>

          <div
            onClick={() => {
              if (open) {
                setOpen(false);
              }
            }}
            className={`${
              open && " inset-0 z-[1000] fixed h-screen w-screen bg-black/90"
            }`}
          >
            {open && (
              <>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (emblaMainApi) {
                      emblaMainApi.scrollPrev();
                    }
                  }}
                  className="prev top-1/2 left-4 absolute z-[200]  -translate-y-1/2 transform rotate-[180deg]"
                >
                  {<MdArrowForwardIos size={48} color="white" />}
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (emblaMainApi) {
                      emblaMainApi.scrollNext();
                    }
                  }}
                  className="next top-1/2 right-4 absolute z-[200]  -translate-y-1/2"
                >
                  <MdArrowForwardIos size={48} color="white" />
                </div>
              </>
            )}

            {open && (
              <div className="absolute flex gap-1 left-1/2 top-4 z-[10] w-max  items-end text-white  bg-themeColor rounded-md p-2 ">
                <CameraIcon />
                <span>
                  {url ? (
                    <span>
                      {selectedIndex + 1} / {slides.length + 1}
                    </span>
                  ) : (
                    <span>
                      {selectedIndex + 1} / {slides.length}
                    </span>
                  )}
                </span>
              </div>
            )}

            <span
              onClick={() => {
                setOpen(false);
              }}
              className={`absolute cursor-pointer  top-5 left-4 ${
                open ? "block" : "hidden"
              } text-xl text-white`}
            >
              <span className="cursor-pointer bg-red-500">
                <FaTimes />
              </span>
            </span>
            <div
              className={`embla__viewport ${
                open && "h-full w-full"
              }  rounded-xl overflow-hidden`}
              ref={emblaMainRef}
            >
              <div
                className={`flex  ${
                  open ? "h-full" : "xs:h-[400px] h-[500px]"
                } `}
              >
                {slides.map((item, index) => (
                  <div
                    onClick={() => {
                      setOpen(true);
                    }}
                    className="embla__slide flex justify-center h-full basis-full shrink-0 "
                    key={index}
                  >
                    <img
                      onClick={(e) => {
                        if (open) {
                          e.stopPropagation();
                        }
                        console.log("clicked");
                      }}
                      className={`object-cover ${
                        open &&
                        "h-auto max-h-[90vh] md:max-h-[600px] block my-auto mx-auto max-w-[90vw] w-auto"
                      } h-full w-full`}
                      src={item}
                      alt=""
                    />
                  </div>
                ))}
                {!variant && url && <VideoComponent url={url || ""} />}
              </div>
            </div>
          </div>
        </div>

        <div
          className={`embla-thumbs ${
            variant ? "h-0 pointer-events-none invisible" : "h-auto"
          }`}
        >
          <div
            className="embla-thumbs__viewport overflow-hidden"
            ref={emblaThumbsRef}
          >
            <div className="flex">
              {slides.map((item, index) => (
                <Thumb
                  key={index}
                  onClick={() => onThumbClick(index)}
                  selected={index === selectedIndex}
                  index={item}
                />
              ))}
              {url && (
                <div
                  className={`!h-[100px] cursor-pointer rounded-xl overflow-hidden shrink-0 relative ${
                    selectedIndex === slides.length &&
                    "border-[2px] border-themeColor"
                  } !w-[150px]`}
                  onClick={() => {
                    onThumbClick(slides.length + 1);
                  }}
                >
                  <img
                    src={getYouTubeThumbnailURL(url) as string}
                    alt="Thumb Image"
                    className="absolute cursor-pointer h-full object-cover w-full"
                  />
                  {/* <VideoComponent
                notClick={true}
                notPlay={true}
                className="!h-[100px] !w-[125px]"
                url="https://www.youtube.com/watch?v=I478PzssnKA"
              /> */}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmblaCarousel;

export const ButtonControls = () => {
  return (
    <svg
      width="60"
      height="60"
      viewBox="0 0 60 60"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="30"
        cy="30"
        r="30"
        transform="matrix(-1 0 0 1 60 0)"
        fill="#212121"
        fillOpacity="0.4"
      />
      <path
        d="M23.418 17.5294C23.2827 17.3863 23.177 17.2179 23.1068 17.0339C23.0366 16.8499 23.0034 16.6539 23.009 16.4571C23.0145 16.2603 23.0588 16.0664 23.1393 15.8867C23.2197 15.707 23.3348 15.5449 23.478 15.4096C23.6211 15.2744 23.7894 15.1687 23.9734 15.0985C24.1574 15.0283 24.3534 14.995 24.5502 15.0006C24.7471 15.0062 24.9409 15.0505 25.1206 15.1309C25.3003 15.2114 25.4625 15.3265 25.5977 15.4696L38.3402 28.9617C38.6034 29.24 38.75 29.6085 38.75 29.9916C38.75 30.3746 38.6034 30.7431 38.3402 31.0215L25.5977 44.515C25.4634 44.6613 25.3013 44.7794 25.1209 44.8625C24.9405 44.9456 24.7454 44.992 24.5469 44.9991C24.3485 45.0061 24.1506 44.9736 23.9647 44.9036C23.7789 44.8335 23.6089 44.7272 23.4645 44.5908C23.3201 44.4545 23.2042 44.2908 23.1236 44.1093C23.043 43.9278 22.9993 43.7321 22.9949 43.5335C22.9906 43.335 23.0258 43.1375 23.0984 42.9527C23.171 42.7678 23.2797 42.5993 23.418 42.4568L35.1891 29.9916L23.418 17.5294Z"
        fill="white"
      />
    </svg>
  );
};

export const CameraIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M9.778 21H14.222C17.343 21 18.904 21 20.025 20.265C20.5088 19.9481 20.9254 19.539 21.251 19.061C22 17.961 22 16.428 22 13.364C22 10.3 22 8.76699 21.251 7.66699C20.9254 7.18898 20.5088 6.77984 20.025 6.46299C19.305 5.98999 18.403 5.82099 17.022 5.76099C16.363 5.76099 15.796 5.27099 15.667 4.63599C15.5684 4.17085 15.3123 3.75402 14.9418 3.45594C14.5714 3.15785 14.1095 2.99679 13.634 2.99999H10.366C9.378 2.99999 8.527 3.68499 8.333 4.63599C8.204 5.27099 7.637 5.76099 6.978 5.76099C5.598 5.82099 4.696 5.99099 3.975 6.46299C3.49154 6.77995 3.07527 7.18907 2.75 7.66699C2 8.76699 2 10.299 2 13.364C2 16.429 2 17.96 2.749 19.061C3.073 19.537 3.489 19.946 3.975 20.265C5.096 21 6.657 21 9.778 21ZM12 9.27299C9.699 9.27299 7.833 11.104 7.833 13.363C7.833 15.622 9.7 17.456 12 17.456C14.3 17.456 16.167 15.624 16.167 13.365C16.167 11.106 14.3 9.27299 12 9.27299ZM12 10.909C10.62 10.909 9.5 12.008 9.5 13.364C9.5 14.719 10.62 15.818 12 15.818C13.38 15.818 14.5 14.719 14.5 13.364C14.5 12.009 13.38 10.909 12 10.909ZM16.722 10.091C16.722 9.63899 17.095 9.27299 17.556 9.27299H18.666C19.126 9.27299 19.5 9.63899 19.5 10.091C19.4979 10.3099 19.409 10.519 19.2528 10.6724C19.0966 10.8257 18.8859 10.9109 18.667 10.909H17.556C17.4475 10.91 17.3399 10.8897 17.2393 10.8492C17.1387 10.8086 17.047 10.7487 16.9696 10.6727C16.8921 10.5968 16.8304 10.5063 16.7879 10.4065C16.7454 10.3067 16.723 10.1995 16.722 10.091Z"
        fill="white"
      />
    </svg>
  );
};

export const planImage = [
  "/images/plan-image-1.webp",
  "/images/plan-image-2.webp",
  "/images/plan-image-3.webp",
  "/images/plan-image-4.webp",
  "/images/plan-image-5.webp",
  "/images/plan-image-6.webp",
  "/images/plan-image-7.webp",
  "/images/plan-image-8.webp",
  // "/images/plan-image-1.webp",
  // "/images/plan-image-1.webp",
  // "/images/plan-image-1.webp",
];
